/*
  ============================================================
  summer-reveal.js — entrada das secoes na rolagem
  ============================================================

  O QUE FAZ:
  Marca cada secao de #MainContent (menos a primeira) e as revela
  conforme entram na viewport, uma a uma, no padrao de animacao que o
  lojista escolheu. Opcionalmente escalona a entrada dos filhos de um
  container marcado com data-summer-reveal-group, para que uma grade de
  cards entre em cascata em vez de tudo de uma vez.

  ONDE E USADO:
  Carregado por layout/theme.liquid com defer, e SO quando a setting
  "Revelar secoes na rolagem" esta ligada. O CSS que ele aciona vive
  na secao 21 de assets/summer-components.css.

  CONTRATO COM O CSS:
  - .summer-reveal-armed no <html>  -> estado pre-JS, posto pelo script
    inline do <head>. Este arquivo o remove assim que assume.
  - .summer-reveal no elemento      -> estado inicial (oculto e deslocado).
  - .is-revealed no elemento        -> estado final (visivel, sem deslocamento).
  - data-summer-motion no elemento  -> qual padrao usar. Quando a secao nao
    declara um, este arquivo escreve o padrao global (--summer-motion-default).
    O valor 'none' significa "nao anime isto".
  - --summer-reveal-delay inline    -> atraso, da cascata ou do atributo
    data-summer-motion-delay.

  QUEM E ARMADO, NESTA ORDEM:
  1. O elemento raiz que a secao renderiza, quando ele traz
     data-summer-motion — o lojista escolheu um padrao para essa secao.
     Nesse caso o wrapper .shopify-section NAO e armado: os dois juntos
     dariam duas entradas encaixadas, com o deslocamento somado e a
     opacidade multiplicada.
  2. O wrapper .shopify-section, quando ninguem dentro escolheu nada.
  3. Os filhos de cada data-summer-reveal-group (cascata).
  4. Qualquer outro elemento com data-summer-motion que tenha sobrado —
     um bloco solto, algo no rodape — desde que nenhum ancestral ja
     esteja armado. E o que permite marcar um elemento a mao sem que ele
     anime junto com a secao inteira, uma animacao dentro da outra.

  OBSERVACOES:
  Sai sem fazer nada, deixando a pagina inteira visivel, quando o
  usuario pediu reducao de movimento ou quando o navegador nao tem
  IntersectionObserver. Nao ha fallback por evento de scroll de
  proposito: um listener de scroll para isso custa mais do que a
  animacao vale.
  Cada elemento e revelado uma vez so — a partir dai o observer para
  de observa-lo. Rolar de volta nao esconde nada.
  Reage a shopify:section:load para continuar funcionando quando o
  lojista adiciona, move ou edita uma secao no editor de temas.
*/
(function () {
  'use strict';

  var ARMED_CLASS = 'summer-reveal-armed';
  var REVEAL_CLASS = 'summer-reveal';
  var VISIBLE_CLASS = 'is-revealed';
  var GROUP_CLASS = 'summer-reveal-group';

  var SECTION_SELECTOR = '#MainContent > .shopify-section';
  var GROUP_SELECTOR = '[data-summer-reveal-group]';
  var OPT_OUT_SELECTOR = '[data-summer-reveal="off"]';

  var MOTION_ATTR = 'data-summer-motion';
  var MOTION_SELECTOR = '[' + MOTION_ATTR + ']';
  var MOTION_DELAY_ATTR = 'data-summer-motion-delay';
  var MOTION_NONE = 'none';
  /* "Sem animacao" vale para o elemento E para tudo dentro dele — por isso e
     um seletor usado com closest(), e nao uma comparacao de valor. */
  var MOTION_OFF_SELECTOR = '[' + MOTION_ATTR + '="' + MOTION_NONE + '"]';

  /* Teto da cascata. Sem ele, uma grade de 24 produtos deixaria o ultimo
     card esperando mais de dois segundos depois do primeiro. */
  var MAX_STAGGER = 6;

  var root = document.documentElement;

  function release() {
    root.classList.remove(ARMED_CLASS);
  }

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) {
    release();
    return;
  }

  var rootStyle = getComputedStyle(root);

  /* O passo da cascata vem da setting, via custom property escrita por
     snippets/css-variables.liquid. Fica no CSS, e nao aqui, para que o valor
     seja um so — o mesmo lugar de onde saem todos os tokens de motion. */
  var step = 90;
  var declared = parseInt(rootStyle.getPropertyValue('--summer-reveal-step'), 10);
  if (!isNaN(declared) && declared >= 0) step = declared;

  /* Padrao global, pela mesma via. Nao e validado contra uma lista: um valor
     desconhecido simplesmente nao casa com regra de padrao nenhuma e o
     elemento entra com o deslocamento base do CSS, que e 'rise'. Uma lista
     aqui seria um segundo lugar para manter a cada padrao novo. */
  var defaultPattern = (rootStyle.getPropertyValue('--summer-motion-default') || '').trim() || 'rise';

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    },
    {
      /* Embaixo, margem negativa: a secao so conta como visivel depois de
         entrar de verdade, nao quando o primeiro pixel encosta na dobra.

         Em cima, margem enorme: tudo que ja passou da tela conta como
         visivel. Sem isso, quem chega ao meio da pagina de uma vez — link
         com ancora, o navegador restaurando a rolagem no botao voltar, um
         Ctrl+End — deixa para tras secoes que nunca chegaram a intersectar
         em quadro nenhum, e elas ficam invisiveis ate o visitante rolar
         PARA CIMA. O observer so avalia os quadros que existiram; um salto
         nao tem quadros no meio. */
      rootMargin: '9999px 0px -10% 0px',
      threshold: 0
    }
  );

  /* Revelar um container de cascata nao o revela sozinho: ele nunca ficou
     oculto. Quem entra sao os filhos, cada um no seu atraso. */
  function reveal(el) {
    if (el.classList.contains(GROUP_CLASS)) {
      children(el).forEach(function (child) {
        child.classList.add(VISIBLE_CLASS);
      });
      return;
    }
    el.classList.add(VISIBLE_CLASS);
  }

  function children(el) {
    return Array.prototype.slice.call(el.children);
  }

  function pattern(el) {
    return (el.getAttribute(MOTION_ATTR) || '').trim();
  }

  function delay(el, ms) {
    el.style.setProperty('--summer-reveal-delay', ms + 'ms');
  }

  /* fallback e o padrao a escrever quando o elemento nao trouxe um. So o
     wrapper de secao recebe isso: quem chega aqui com atributo proprio ja
     escolheu, e quem chega sem atributo e por outro caminho (cascata,
     elemento solto) herda o deslocamento base do CSS. */
  function arm(el, fallback) {
    if (el.classList.contains(REVEAL_CLASS)) return false;
    if (fallback && !el.hasAttribute(MOTION_ATTR)) el.setAttribute(MOTION_ATTR, fallback);
    el.classList.add(REVEAL_CLASS);

    var own = parseInt(el.getAttribute(MOTION_DELAY_ATTR), 10);
    if (!isNaN(own) && own > 0) delay(el, own);

    return true;
  }

  /* Prepara os filhos de um container em cascata. Eles NAO sao observados um
     a um: quem dispara e o container, e cada filho so espera a propria vez.
     Observar 24 cards separadamente daria 24 entradas independentes,
     disparadas em ordem de rolagem, e a cascata viraria ruido.

     O atraso do indice sobrescreve um data-summer-motion-delay que o filho
     porventura traga: dentro de uma cascata, quem manda na ordem e a cascata. */
  function armGroup(group) {
    if (group.classList.contains(GROUP_CLASS)) return;
    group.classList.add(GROUP_CLASS);
    children(group).forEach(function (child, index) {
      arm(child);
      delay(child, Math.min(index, MAX_STAGGER) * step);
    });
    observer.observe(group);
  }

  function armSection(section) {
    /* O opt-out pode estar no wrapper .shopify-section (via "class" no schema)
       ou no elemento raiz que a section renderiza. Os dois valem. */
    if (section.matches(OPT_OUT_SELECTOR) || section.querySelector(':scope > ' + OPT_OUT_SELECTOR)) {
      return;
    }

    var chosen = section.querySelector(':scope > ' + MOTION_SELECTOR);

    if (chosen) {
      /* "Sem animacao" no editor. Vale para a secao inteira, cascata de
         cards inclusive — meia animacao nao e o que "sem animacao" quer
         dizer. */
      if (pattern(chosen) === MOTION_NONE) return;
      if (arm(chosen)) observer.observe(chosen);
    } else if (section.previousElementSibling && arm(section, defaultPattern)) {
      /* Sem :first-child no seletor CSS: no editor de temas a ordem muda e um
         elemento ja armado continuaria armado. Quem e a primeira secao e
         resolvido aqui, sempre pelo estado atual do DOM. */
      observer.observe(section);
    }

    Array.prototype.slice.call(section.querySelectorAll(GROUP_SELECTOR)).forEach(armGroup);
    armLoose(section);
  }

  /* Elementos marcados a mao que nenhum dos passos anteriores alcancou.
     Descendente de algo ja armado fica de fora: duas entradas encaixadas
     multiplicam opacidade e somam deslocamento, e o resultado nao e o que
     nenhum dos dois padroes descreve. */
  function armLoose(scope) {
    Array.prototype.slice.call(scope.querySelectorAll(MOTION_SELECTOR)).forEach(function (el) {
      if (el.classList.contains(REVEAL_CLASS)) return;
      if (el.closest(MOTION_OFF_SELECTOR) || el.closest(OPT_OUT_SELECTOR)) return;
      if (el.parentElement && el.parentElement.closest('.' + REVEAL_CLASS)) return;
      if (arm(el)) observer.observe(el);
    });
  }

  function scan() {
    Array.prototype.slice.call(document.querySelectorAll(SECTION_SELECTOR)).forEach(armSection);
    /* Fora de #MainContent tambem: cabecalho e rodape sao section groups e
       nunca passam por armSection. */
    armLoose(document);
  }

  scan();
  /* Depois de armar: as secoes trocam a regra generica do <html> pela classe
     propria no mesmo tick, entao nao ha quadro nenhum em que apareçam. */
  release();

  /* Editor de temas: secao adicionada, movida ou re-renderizada. */
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section || !section.closest) return;
    if (section.closest('#MainContent')) {
      armSection(section);
      return;
    }
    /* Cabecalho, rodape e qualquer outro section group. */
    armLoose(section);
  });
})();
